import {isUndefined} from 'lodash';
import React, {FC, useMemo} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {AnswerFolderModel} from '../../../../models/api/answerFolderModel';
import {DropdownItemIcon} from '../dropdownItemIcon';
import {DeprecatedSearchDropdown} from '../search/deprecatedSearchDropdown';
import {AsyncDropdownSource, DropdownFetcher} from '../source/asyncDropdownSource';
import {RowRendererOf} from '../source/dropdownSource';
import {AnswerFolderDropdownItemWrapper} from './answerFolderDropdownItemWrapper';
import {AnswerFolderSearchResultsDropdownItemContainer} from './answerFolderSearchResultsDropdownItemContainer';

/*
 * Props.
 */

interface AnswerFolderDropdownProps extends DropdownProps {
  onFolderSelect: HandlerOf<number | undefined>;
  fetchAnswerFoldersAsync: DropdownFetcher<AnswerFolderModel | null>;
  selectedFolder: AnswerFolderModel | undefined;
}

/*
 * Component.
 */

export const AnswerFolderDropdown: FC<AnswerFolderDropdownProps> = (props) => {
  const {selectedFolder, onFolderSelect} = props;

  const renderRow: RowRendererOf<AnswerFolderModel | null> = useMemo(
    () => (folder, index, _folders, isSearching) => {
      if (folder === null) {
        return renderNoneDropdownItem(index, selectedFolder, onFolderSelect);
      }

      if (isSearching) {
        return renderFolderSearchResultsDropdownItem(folder, index, selectedFolder, onFolderSelect);
      }

      return renderFolderDropdownItem(folder, index, selectedFolder, onFolderSelect);
    },
    [selectedFolder, onFolderSelect],
  );

  const dropdownSource = useMemo(
    () =>
      new AsyncDropdownSource({
        fetcher: props.fetchAnswerFoldersAsync,
        renderRow,
      }),
    [props.fetchAnswerFoldersAsync, renderRow],
  );

  return (
    <DeprecatedSearchDropdown
      {...props} /* eslint-disable-line react/jsx-props-no-spreading */
      title={<Trans id="answer-folder-dropdown-title">Folders</Trans>}
      height={300}
      source={dropdownSource}
    />
  );
};

function renderNoneDropdownItem(
  index: number,
  selectedFolder: AnswerFolderModel | undefined,
  onFolderSelect: HandlerOf<number | undefined>,
) {
  const isChecked = isUndefined(selectedFolder);

  return (
    <DropdownItem
      key="no-answer-folder"
      index={index}
      isChecked={isChecked}
      onSelect={() => onFolderSelect(undefined)}
    >
      <DropdownItemIcon label={null} iconName="noneSmall" />
      <DropdownItemContent isChecked={isChecked}>
        <Trans id="answer-folder-dropdown-none-selected">None</Trans>
      </DropdownItemContent>
    </DropdownItem>
  );
}

function renderFolderSearchResultsDropdownItem(
  folder: AnswerFolderModel,
  index: number,
  selectedFolder: AnswerFolderModel | undefined,
  onFolderSelect: HandlerOf<number | undefined>,
) {
  return (
    <AnswerFolderSearchResultsDropdownItemContainer
      folder={folder}
      index={index}
      selectedFolder={selectedFolder}
      onFolderSelect={onFolderSelect}
    />
  );
}

function renderFolderDropdownItem(
  folder: AnswerFolderModel,
  index: number,
  selectedFolder: AnswerFolderModel | undefined,
  onFolderSelect: HandlerOf<number | undefined>,
) {
  const nestingLevel = folder.folderPath.length;

  return (
    <AnswerFolderDropdownItemWrapper
      folder={folder}
      index={index}
      nestingLevel={nestingLevel}
      isChecked={selectedFolder?.id === folder.id}
      onFolderSelect={onFolderSelect}
    >
      {folder.name}
    </AnswerFolderDropdownItemWrapper>
  );
}
